import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  ProductTest,
  ProductTestMutationUpdateArgs,
  ProductTestUpdateFailure,
  UpdateProductTestMutation,
} from "@/graphql/codegen/graphql"
import { UpdateProductTestDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseDeleteProductTestReturn {
  deleteProductTest: (productTest: ProductTest) => Promise<boolean>
  isDeleting: boolean
}

export const useDeleteProductTest = (): UseDeleteProductTestReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<UpdateProductTestMutation, ProductTestMutationUpdateArgs>(
    UpdateProductTestDocument
  )

  const deleteProductTest = useCallback(
    async ({
      productTestId,
      manufacturingDate,
      expirationDate,
    }: Pick<ProductTest, "productTestId" | "manufacturingDate" | "expirationDate">): Promise<boolean> => {
      const { data, error } = await executeMutation({
        input: { productTestId, isDeleted: true, isLotUpdated: false, manufacturingDate, expirationDate },
      })
      const output = data?.productTest.update

      if (error || output?.__typename === "ProductTestUpdateFailure") {
        const message = error?.message ?? (output as ProductTestUpdateFailure).error.message
        notify({ type: NotificationType.Error, message })

        return false
      }

      return true
    },
    [executeMutation, notify]
  )

  return { deleteProductTest, isDeleting: fetching }
}
